import clsx from 'clsx';

type SkeletonProps = {
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular';
  width?: string | number;
  height?: string | number;
};

export function Skeleton({ className, variant = 'rectangular', width, height }: SkeletonProps) {
  return (
    <div
      className={clsx(
        'animate-pulse bg-gray-200',
        variant === 'circular' && 'rounded-full',
        variant === 'text' && 'rounded',
        variant === 'rectangular' && 'rounded-lg',
        className
      )}
      style={{ width, height }}
    />
  );
}

export function StatCardSkeleton() {
  return (
    <div className="card">
      <div className="flex items-center gap-3">
        <Skeleton className="w-10 h-10" variant="rectangular" />
        <div className="flex-1">
          <Skeleton className="w-16 h-3 mb-2" variant="text" />
          <Skeleton className="w-12 h-6" variant="text" />
        </div>
      </div>
    </div>
  );
}

export function ActionCardSkeleton() {
  return (
    <div className="card">
      <div className="flex items-start gap-4">
        <Skeleton className="w-12 h-12" variant="circular" />
        <div className="flex-1">
          <Skeleton className="w-24 h-3 mb-2" variant="text" />
          <Skeleton className="w-48 h-5 mb-3" variant="text" />
          <Skeleton className="w-full h-4 mb-2" variant="text" />
          <Skeleton className="w-32 h-4" variant="text" />
        </div>
      </div>
    </div>
  );
}

export function TableRowSkeleton({ columns = 4 }: { columns?: number }) {
  return (
    <div className="flex items-center gap-4 p-4 border-b border-gray-100">
      {Array.from({ length: columns }).map((_, i) => (
        <Skeleton key={i} className="flex-1 h-4" variant="text" />
      ))}
    </div>
  );
}

export function DashboardSkeleton() {
  return (
    <div className="space-y-8 animate-in fade-in duration-300">
      {/* Header */}
      <div>
        <Skeleton className="w-48 h-8 mb-2" variant="text" />
        <Skeleton className="w-64 h-4" variant="text" />
      </div>

      {/* Hero Card */}
      <div className="card">
        <div className="text-center py-8">
          <Skeleton className="w-20 h-20 mx-auto mb-6" variant="circular" />
          <Skeleton className="w-64 h-6 mx-auto mb-2" variant="text" />
          <Skeleton className="w-96 h-4 mx-auto mb-8" variant="text" />
          <div className="flex justify-center gap-6">
            <Skeleton className="w-32 h-16" variant="rectangular" />
            <Skeleton className="w-32 h-16" variant="rectangular" />
            <Skeleton className="w-32 h-16" variant="rectangular" />
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCardSkeleton />
        <StatCardSkeleton />
        <StatCardSkeleton />
        <StatCardSkeleton />
      </div>

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ActionCardSkeleton />
        <ActionCardSkeleton />
        <ActionCardSkeleton />
      </div>
    </div>
  );
}

export function AnalyticsSkeleton() {
  return (
    <div className="space-y-8 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Skeleton className="w-48 h-8 mb-2" variant="text" />
          <Skeleton className="w-64 h-4" variant="text" />
        </div>
        <Skeleton className="w-32 h-10" variant="rectangular" />
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCardSkeleton />
        <StatCardSkeleton />
        <StatCardSkeleton />
        <StatCardSkeleton />
      </div>

      {/* Top Performers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ActionCardSkeleton />
        <ActionCardSkeleton />
      </div>

      {/* Recent Sales */}
      <div className="card">
        <Skeleton className="w-32 h-5 mb-4" variant="text" />
        <div className="space-y-3">
          <TableRowSkeleton columns={3} />
          <TableRowSkeleton columns={3} />
          <TableRowSkeleton columns={3} />
        </div>
      </div>
    </div>
  );
}
